/** Modos de runtime UDV (lab vs público). */
export const UDV_MODES = {
  DEVELOPER: 'developer',
  PRODUCTION: 'production',
}

/**
 * Planes comerciales + perfil portal.
 * `demo` = showcase limitado (debajo de lite).
 * `patient` = portal paciente (2D básico; sin features avanzadas).
 */
export const UDV_PLANS = {
  PATIENT: 'patient',
  DEMO: 'demo',
  LITE: 'lite',
  PRO: 'pro',
  PREMIUM: 'premium',
  /** Plan root solo en developer (dev-udv / localhost): todas las features + beta. */
  SUPREMO: 'supremo',
}

/** Rank para clamp (upgrade no permitido por encima del token). */
export const PLAN_RANK = {
  [UDV_PLANS.PATIENT]: 0,
  [UDV_PLANS.DEMO]: 1,
  [UDV_PLANS.LITE]: 2,
  [UDV_PLANS.PRO]: 3,
  [UDV_PLANS.PREMIUM]: 4,
  [UDV_PLANS.SUPREMO]: 5,
}

/** Planes comerciales vendibles (panel DEVELOPER / simulate). */
export const UDV_PLAN_LIST = [
  UDV_PLANS.DEMO,
  UDV_PLANS.LITE,
  UDV_PLANS.PRO,
  UDV_PLANS.PREMIUM,
]

/** Simulate en lab: supremo (default) + comerciales. */
export const UDV_DEVELOPER_SIM_PLANS = [
  UDV_PLANS.SUPREMO,
  ...UDV_PLAN_LIST,
]

export const PLAN_QUERY_KEY = 'plan'
export const SHELL_QUERY_KEY = 'shell'
/** Paleta chrome: dark | brand | light (OHIF / Oviyam). */
export const THEME_QUERY_KEY = 'theme'

/** Temas de cromo (independientes del shell). */
export const UDV_CHROME_THEMES = {
  DARK: 'dark',
  BRAND: 'brand',
  LIGHT: 'light',
}

export const UDV_CHROME_THEME_LIST = [
  UDV_CHROME_THEMES.DARK,
  UDV_CHROME_THEMES.BRAND,
  UDV_CHROME_THEMES.LIGHT,
]
export const TOKEN_QUERY_KEY = 'udvToken'
export const TICKET_QUERY_KEY = 't'
/** Worklist clínico opaco (CONTRATO-VIEWER-WORKLIST.md). */
export const WORKLIST_QUERY_KEY = 'w'
export const PORTAL_QUERY_KEY = 'portal'
export const INVITE_QUERY_KEY = 'udvInvite'
export const PERSONA_QUERY_KEY = 'persona'
/** Origen del estudio / clínica (Controller edge_id). */
export const EDGE_ID_QUERY_KEY = 'edge_id'
/** JWT one-shot de elevación plataforma → UDV (90s). */
export const ELEV_QUERY_KEY = 'elev'
/** Hint de contexto iframe Workspace (`embed=workspace`). No autoriza plan. */
export const EMBED_QUERY_KEY = 'embed'
/** postMessage parent → UDV para elevación Workspace (CONTRATO-UDV-WORKSPACE-EMBED.md). */
export const WORKSPACE_ELEVATE_MESSAGE_TYPE = 'udv.workspace.elevate'
export const WORKSPACE_ELEVATE_MESSAGE_VERSION = 1

/** Purpose persistido en ticket plataforma (CONTRATO-UDV-ELEVATION.md). */
export const TICKET_PURPOSE = {
  PATIENT_SHARE: 'patient_share',
  CLINICAL: 'clinical',
}

/** Personas UDV (portal paciente vs clínico). */
export const UDV_PERSONAS = {
  PORTAL: 'portal',
  CLINICAL: 'clinical',
}

/** Features cableadas en Fase 1 (+ drafts de catálogo). */
export const FEATURE_IDS = {
  MPR: 'mpr',
  MPR_SLAB: 'mprSlab',
  VOL3D: 'vol3d',
  PET_FUSION: 'petFusion',
  DSA: 'dsa',
  VOICE_NOTES: 'voiceNotes',
  VOICE_WHISPER_LOCAL: 'voiceWhisperLocal',
  ANALYSIS_BODY_COMP: 'analysisBodyComp',
  AI_COPILOT: 'aiCopilot',
}

export const FEATURE_STATUS = {
  DRAFT: 'draft',
  PUBLISHED: 'published',
}

export const STORAGE_KEYS = {
  CATALOG_OVERRIDES: 'udv.plan.catalogOverrides',
  TOKEN_OVERRIDES: 'udv.plan.tokenOverrides',
  SIMULATE_PLAN: 'udv.plan.simulate',
  /** Lab: simular theme chrome (dark|brand|light). */
  SIMULATE_THEME: 'udv.plan.simulateTheme',
  /** Lab: forzar shell (classic|edge|studio|ohif|oviyamng). */
  SIMULATE_SHELL: 'udv.plan.simulateShell',
  SHOW_DRAFT: 'udv.plan.showDraft',
  ENTITLEMENTS: 'udv.entitlements',
  /** Lab: simular perfil portal patient (switch / ?portal=1). */
  PORTAL_SIM: 'udv.plan.portalSim',
  /** Lab: overrides de secciones chrome portal. */
  PORTAL_CHROME: 'udv.plan.portalChrome',
  /** Persona persistida: portal | clinical */
  PERSONA: 'udv.persona',
  /** Snapshot links[] del Controller (multiclínica). */
  CONTROLLER_LINKS: 'udv.controller.links',
}

export const PLAN_CHANGE_EVENT = 'udv-plan-changed'

export const ACCESS_REASONS = {
  MISSING_TOKEN: 'missing_token',
  INVALID_TOKEN: 'invalid_token',
  DISABLED_TOKEN: 'disabled_token',
  /** Production sin ticket/invite/Controller ni offline (legacy udvToken ya no cuenta). */
  MISSING_ENTITLEMENTS: 'missing_entitlements',
  /** links[] sin plan (Controller viejo / vínculo sin asignar). */
  MISSING_CONTROLLER_PLAN: 'missing_controller_plan',
  /** Ticket inválido / expirado / resolve falló. */
  INVALID_TICKET: 'invalid_ticket',
  /** Worklist inválido / expirado / resolve falló. */
  INVALID_WORKLIST: 'invalid_worklist',
  /** Estudio aún no disponible (ingest). */
  TICKET_PENDING: 'ticket_pending',
  /** Invite inválido / expirado / revocado. */
  INVALID_INVITE: 'invalid_invite',
  /** Invite pendiente: requiere login Controller. */
  INVITE_LOGIN_REQUIRED: 'invite_login_required',
  /** Workspace embed sin elev/postMessage válido (abrir desde plataforma). */
  WORKSPACE_EMBED_REQUIRED: 'workspace_embed_required',
}

/** Origen del entitlement persistido. */
export const ENTITLEMENT_SOURCES = {
  URL: 'url',
  CONTROLLER: 'controller',
  TICKET: 'ticket',
  INVITE: 'invite',
  /** Plan desde links[] del origen edge_id en la URL. */
  ORIGIN_LINK: 'origin_link',
  /** Plan canjeado vía POST udv-elevation (JWT elev, one-shot). */
  PLATFORM_ELEVATION: 'platform_elevation',
  /** Plan canjeado en iframe Workspace (postMessage elev; no Ver/ticket URL). */
  WORKSPACE_EMBED: 'workspace_embed',
}
